// <comparison-table> — the scroll-float (parallax) layer for the `siclips-comparison-table` section.
// The table + highlighted column + icons are pure CSS; this only makes the desktop product image
// "float": as you scroll, the image drifts vertically proportional to how far its column is from the
// viewport centre — `shift = -fromCenter * speed`, clamped to ±amount px (the classic reference
// parallax). Measured on the media WRAPPER (untransformed) and applied to the IMAGE, so there is no
// measure→transform feedback loop. Throttled to one update per animation frame per scroll. Desktop
// only, never under prefers-reduced-motion, and the configured base offset (--sct-img-offset) is
// preserved (the drift is added on top of it). Listeners are removed on disconnect.

class ComparisonTable extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.media = this.querySelector('[data-sct-parallax]');
    this.img = this.media ? this.media.querySelector('.sct__img') : null;
    this.enabled = this.dataset.parallaxOn !== 'false';
    this.amount = Math.max(0, parseFloat(this.dataset.parallax) || 0);        // max drift, px
    this.speed = Math.max(0, parseFloat(this.dataset.parallaxSpeed) || 0.15); // px shift per px off-centre
    this.rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.desktopq = window.matchMedia('(min-width: 750px)');
    this._ticking = false;

    if (!this.img || !this.enabled || this.amount === 0) return;

    // base offset from the CSS var, so the float stacks on top of the configured offset
    this._base = parseFloat(getComputedStyle(this.img).getPropertyValue('--sct-img-offset')) || 0;

    this._onScroll = () => {
      if (this._ticking) return;
      this._ticking = true;
      window.requestAnimationFrame(() => this._update());
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
    this._update();
  }

  disconnectedCallback() {
    this._init = false;
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
  }

  _update() {
    this._ticking = false;
    // no float on mobile or under reduced motion — restore the configured base offset (CSS) and bail
    if (!this.desktopq.matches || this.rmq.matches) {
      this.img.style.transform = '';
      return;
    }
    const r = this.media.getBoundingClientRect(); // the WRAPPER — never transformed, so no feedback
    const vh = window.innerHeight || document.documentElement.clientHeight || 1;
    const fromCenter = (r.top + r.height / 2) - vh / 2;
    let shift = -fromCenter * this.speed;
    if (shift > this.amount) shift = this.amount;
    if (shift < -this.amount) shift = -this.amount;
    this.img.style.transform = `translate3d(0, ${(this._base + shift).toFixed(1)}px, 0)`;
  }
}

if (!customElements.get('comparison-table')) {
  customElements.define('comparison-table', ComparisonTable);
}
